import WarehouseImportGroups, { ImportGroups } from '../../../ProductWarehouse/WarehouseImportGroups';
import ModalCloseButton from '../../../../../Component/UI/ModalCloseButton';
import React, { useRef, useState } from "react";
import styled from "styled-components";
import { fontStyles } from "../../../../../Component/style/admin_font";
import { CenterView, RowView } from "../../../../../Component/style/common_style";
import ExcelPopup from "./ExcelPopup";
import MatchResultTable from "./MatchResultTable";
import { FilterTab } from "./types";
import useExcelUpload from "./useExcelUpload";

const TABS: FilterTab[] = ["전체", "성공", "없음", "오류"];

interface Props {
  visible: boolean;
  onClose: () => void;
  liveSeq: number;
  warehouse?: boolean;
  onSuccess: () => void;
}

const ExcelUploadModal = ({ visible, onClose, liveSeq, onSuccess, warehouse }: Props) => {
  const [groups, setGroups] = useState<ImportGroups>({ groupSeqs: [] });
  const excelInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const {
    groupLocked,
    excelName,
    imageUploadNames,
    imageFileNames,
    uploading,
    processing,
    uploadProgress,
    filter,
    setFilter,
    matchRows,
    counts,
    tabCounts,
    unusedImages,
    popup,
    closePopup,
    hasExcel,
    actions,
  } = useExcelUpload(liveSeq, () => { onSuccess(); onClose(); }, warehouse, groups);

  const busy = uploading || processing;
  const groupInvalid = warehouse && groups.newGroupName !== undefined && !groups.newGroupName.trim();

  if (!visible) return null;

  const close = () => {
    if (busy) return;
    actions.resetAll();
    onClose();
  };

  const onExcelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) actions.onSelectExcel(f);
    e.target.value = "";
  };
  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fs = e.target.files;
    if (fs?.length) actions.addImages(Array.from(fs));
    e.target.value = "";
  };

  const allowDrop = (e: React.DragEvent) => e.preventDefault();
  const dropExcel = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) actions.onSelectExcel(f);
  };
  const dropImage = (e: React.DragEvent) => {
    e.preventDefault();
    const fs = e.dataTransfer.files;
    if (fs?.length) actions.addImages(Array.from(fs));
  };

  return (
    <Overlay data-admin-modal-overlay="" onClick={close}>
      <Content data-admin-modal="" role="dialog" aria-modal="true" aria-label="엑셀 업로드" onClick={(e) => e.stopPropagation()}>
        <Header data-admin-modal-header="">
          <div className="title">엑셀 업로드</div>
          <CloseIcon tone="admin" aria-label="엑셀 업로드 닫기" onClick={close} />
        </Header>

        <BodyRow data-admin-modal-body="">
          {/* 좌측: 업로드 */}
          <LeftCol>
            {warehouse && <WarehouseImportGroups value={groups} onChange={setGroups} disabled={busy || groupLocked} />}
            <FieldLabel>
              엑셀 등록 <span>*</span>
            </FieldLabel>
            <DropBox
              onClick={() => !busy && excelInputRef.current?.click()}
              onDragOver={allowDrop}
              onDrop={dropExcel}
            >
              <UploadIco
                src={require("../../../../../Image/Admin/ic_excel_upload.png")}
              />
              <span>클릭 혹은 파일을 이곳에 드롭하세요.</span>
            </DropBox>
            {excelName && (
              <FileChip>
                <span className="name" title={excelName}>
                  {excelName}
                </span>
                <RemoveFileButton type="button" aria-label={`${excelName} 엑셀 삭제`}
                  disabled={busy} onClick={actions.onDeleteExcel}>×</RemoveFileButton>
              </FileChip>
            )}
            <TemplateDownload
              href={`${process.env.PUBLIC_URL}/templates/product-upload-template.xlsx`}
              download="상품등록_기본양식.xlsx"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 3v12m-5-5 5 5 5-5M5 16v4h14v-4" />
              </svg>
              기본 엑셀 다운로드
            </TemplateDownload>

            <FieldLabel style={{ marginTop: "1.2rem" }}>이미지 등록</FieldLabel>
            <DropBox
              onClick={() => !busy && imageInputRef.current?.click()}
              onDragOver={allowDrop}
              onDrop={dropImage}
            >
              <UploadIco src={require("../../../../../Image/Admin/ic_excel_upload.png")} />
              <span>클릭 혹은 파일을 이곳에 드롭하세요.</span>
            </DropBox>

            <Hint>* 업로드 형식 : jpg, png, zip</Hint>
            {imageUploadNames.length > 0 && (
              <ImageFileList aria-label="등록한 이미지 파일" aria-live="polite">
                {Array.from(new Set([...imageUploadNames, ...imageFileNames])).map((name) => (
                  <FileChip key={name}>
                    <span className="name" title={name}>{name}</span>
                    {imageFileNames.includes(name) && (
                      <RemoveFileButton type="button" aria-label={`${name} 이미지 삭제`}
                        disabled={busy} onClick={() => actions.removeImage(name)}>×</RemoveFileButton>
                    )}
                  </FileChip>
                ))}
              </ImageFileList>
            )}

            <input
              type="file"
              ref={excelInputRef}
              disabled={busy}
              accept=".xlsx,.xls"
              onChange={onExcelChange}
              style={{ display: "none" }}
            />
            <input
              type="file"
              ref={imageInputRef}
              disabled={busy}
              accept="image/*,.zip"
              multiple
              onChange={onImageChange}
              style={{ display: "none" }}
            />
          </LeftCol>

          {/* 우측: 매칭 결과 */}
          <RightCol>
            <ResultHead>
              <Counters>
                {counts.totalImages === 0 ? (
                  <span className="title">매칭 결과 0</span>
                ) : (
                  <>
                    <span className="title">매칭 결과</span>
                    <span className="item">
                      총 이미지 <b>{counts.totalImages}</b>
                    </span>
                    <span className="item">
                      매칭 완료 <b>{counts.matched}</b>
                    </span>
                    <span
                      className="item link"
                      onClick={actions.showUnused}
                    >
                      사용 안함 <b>{counts.unused}</b>
                    </span>
                  </>
                )}
              </Counters>

              <Tabs>
                {TABS.map((t) => (
                  <Tab
                    key={t}
                    className={filter === t ? "active" : ""}
                    onClick={() => setFilter(t)}
                  >
                    {t}
                    {t !== "전체" && ` (${tabCounts[t]})`}
                  </Tab>
                ))}
              </Tabs>
            </ResultHead>

            <MatchResultTable
              rows={matchRows}
              previewImages={!hasExcel && filter === "전체" ? unusedImages : []}
              emptyMessage={filter === "전체" ? undefined : "해당 상태의 결과가 없습니다."}
            />
          </RightCol>
        </BodyRow>

        <Footer data-admin-modal-footer="">
          <CompleteBtn
            className={hasExcel && !busy && !groupInvalid ? "" : "disabled"}
            role="status"
            onClick={hasExcel && !busy && !groupInvalid ? actions.onComplete : undefined}
          >
            {uploading ? uploadProgress : processing ? "파일 확인 중…" : "엑셀 업로드 완료"}
          </CompleteBtn>
        </Footer>

        {popup && <ExcelPopup popup={popup} onClose={closePopup} />}
      </Content>
    </Overlay>
  );
};

export default ExcelUploadModal;

const Overlay = styled(CenterView)`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 1000;
`;

const Content = styled.div`

  box-sizing: border-box;
  width: 94%;
  max-width: 1200px;
  height: 92dvh;
  max-height: 62rem;
  background: var(--admin-theme-surface, white);
  border-radius: 12px;
  padding: 1.6rem 1.8rem;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  @media (max-width: 760px) { width: calc(100% - 24px); padding: 16px; }
`;

const Header = styled(RowView)`
  min-height: 44px;
  flex-shrink: 0;
  .title { overflow-wrap: anywhere; }
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.2rem;
  .title {
    font-size: 19px;
    ${fontStyles.Bold}
    color: var(--admin-ink);
  }
`;

const CloseIcon = styled(ModalCloseButton)`

`;

const BodyRow = styled(RowView)`

  flex: 1;
  gap: 1.5rem;
  align-items: stretch;
  min-height: 0;
  @media (max-width: 760px) { flex-direction: column; overflow-y: auto; }
`;

const LeftCol = styled.div`

  width: 16rem;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  min-height: 0;
  padding-right: var(--space-1);
  @media (max-width: 760px) { width: 100%; overflow-y: visible; }
`;

const FieldLabel = styled.div`
  font-size: 16px;
  ${fontStyles.semiBold}
  color: var(--admin-ink);
  margin-bottom: 0.6rem;
  span {
    color: var(--admin-theme-danger, #eb003b);
  }
`;

const DropBox = styled(CenterView)`
  flex-direction: column;
  gap: 0.5rem;
  height: 8.5rem;
  border: 1px dashed var(--admin-theme-border, #dcdcdc);
  border-radius: 8px;
  background: var(--admin-theme-surface, #fbfbfb);
  cursor: pointer;
  color: var(--admin-theme-muted, #a0a0a0);
  font-size: 14px;
  transition: border 0.2s, background 0.2s;
  &:hover {
    border-color: var(--admin-theme-link, var(--admin-accent));
    background: var(--admin-theme-accent-soft, #f9f7ff);
  }
`;

const UploadIco = styled.img`
  filter: var(--admin-theme-icon-filter, none);
  width: 1.6rem;
  height: 1.6rem;
  opacity: 0.5;
`;

const FileChip = styled(RowView)`
  align-items: center;
  justify-content: space-between;
  margin-top: 0.6rem;
  height: 3rem;
  padding: 0 0.9rem;
  border: 1px solid var(--admin-theme-border, #ececec);
  border-radius: 8px;
  .name {
    flex: 1;
    font-size: 13px;
    color: var(--admin-ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ImageFileList = styled.div`
  max-height: 10rem;
  overflow-y: auto;
`;

const Hint = styled.div`
  margin-top: 0.8rem;
  font-size: 13px;
  color: var(--admin-theme-danger, #eb003b);
  ${fontStyles.regular}
`;

const RemoveFileButton = styled.button`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  margin-left: 8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--admin-muted);
  font-size: 20px;
  cursor: pointer;
  &:hover { background: var(--admin-theme-raised, #f0f0f0); }
  &:focus-visible { outline: 2px solid var(--admin-accent); }
  &:disabled { cursor: default; opacity: 0.5; }
`;

const TemplateDownload = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-sizing: border-box;
  min-height: 44px;
  flex-shrink: 0;
  margin-top: 0.6rem;
  padding: 10px 14px;
  border: 1px solid var(--admin-theme-border, #dcdcdc);
  border-radius: 8px;
  background: var(--admin-theme-surface, #fff);
  color: var(--admin-theme-link, var(--admin-accent-deep));
  font-size: 13px;
  ${fontStyles.semiBold}
  text-decoration: none;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;

  &:hover {
    border-color: var(--admin-theme-link, var(--admin-accent));
    background: var(--admin-theme-accent-soft, #f4f1fe);
  }

  &:focus-visible {
    outline: 2px solid var(--admin-accent);
    outline-offset: 3px;
  }

  svg { flex-shrink: 0; }
`;

const RightCol = styled.div`

  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  @media (max-width: 760px) { flex: none; min-height: 320px; }
`;

const ResultHead = styled(RowView)`
  flex-wrap: wrap; gap: 12px;

  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.8rem;
`;

const Counters = styled(RowView)`
  align-items: center;
  gap: 0.6rem;
  font-size: 15px;
  color: var(--admin-theme-muted, #757575);
  ${fontStyles.medium}
  .title {
    ${fontStyles.semiBold}
    color: var(--admin-ink);
    margin-right: 0.2rem;
  }
  .item {
    padding: 0.45rem 0.8rem;
    border-radius: 6px;
    background: var(--admin-theme-surface, #fbfbfb);
  }
  .item b {
    color: var(--admin-ink);
    ${fontStyles.semiBold}
    margin-left: 0.15rem;
  }
  .link {
    background: var(--admin-theme-accent-soft, #f4f1fe);
    color: var(--admin-theme-link, var(--admin-accent-deep));
    cursor: pointer;
  }
  .link b {
    color: var(--admin-theme-link, var(--admin-accent-deep));
  }
  .link:hover {
    opacity: 0.85;
  }
`;

const Tabs = styled(RowView).attrs<{ 'data-admin-tabs'?: string }>({ 'data-admin-tabs': '' })`
  gap: 0.9rem;
  font-size: 15px;
`;

const Tab = styled.div.attrs<{ 'data-admin-tab'?: string }>({ 'data-admin-tab': '' })`
  cursor: pointer;
  color: var(--admin-theme-muted, #b0b0b0);
  ${fontStyles.medium}
  &.active {
    color: var(--admin-ink);
    ${fontStyles.Bold}
    text-decoration: underline;
    text-underline-offset: 4px;
  }
`;

const Footer = styled(RowView)`

  justify-content: flex-end;
  padding-top: 1.2rem;
  flex-shrink: 0;
  @media (max-width: 760px) { > * { width: 100%; } }
`;

const CompleteBtn = styled(CenterView)`
  width: 12rem;
  height: 3.2rem;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--admin-accent), var(--admin-accent-deep));
  color: white;
  font-size: 15px;
  ${fontStyles.Bold}
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
  &.disabled {
    background: var(--admin-theme-raised, #ececec);
    color: var(--admin-theme-muted, #b0b0b0);
    cursor: not-allowed;
    &:hover {
      opacity: 1;
    }
  }
`;
